import {
  DestroyRef,
  Injectable,
  Injector,
  computed,
  effect,
  inject,
  signal,
  untracked,
  type EffectRef,
  type Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subscription, catchError, exhaustMap, retry, timeout, timer } from 'rxjs';
import { PhotoUploadCoordinatorService } from './photo-upload-coordinator.service';
import { PersonImportStore } from '../+state/person-import.store';
import { isFolderProfile } from '@lite-workspace/import-pipeline-core';
import type { ImportOverrides, ImportSet, ImportTableInfo } from '@lite-workspace/import-pipeline-core';
import { PersonImportApiService, isDefiniteOff, type ImportRun } from '../infrastructure/person-import-api.service';
import { captionsByFile, personRoleCounts, previewRows, rowCounts, tableNames } from '../utils/person-import.util';

const POLL_INTERVAL_MS = 10_000;
const POLL_TIMEOUT_MS = 30_000;
/** Единственная повторная дочитка набора после отказа сети. */
const SET_RETRY_DELAY_MS = 3_000;
/**
 * Потолок ЖИВОГО прогона: дальше сервер не дожимает, он сдаётся. Дедлайн ожидания лица на бэкенде —
 * IMPORT_PENDING_FACE_DEADLINE_MIN (по умолчанию 30 минут, persons-service constants.ts), плюс запас
 * на крон, который его закрывает. Без потолка живой PARTIAL держал бы опрос и «Получаем имя…» вечно.
 */
const LIVE_RUN_CEILING_MS = 35 * 60_000;
/**
 * Сколько ждём прогон, которого сервер ещё не создал: волна закрывается кроном, а не мгновенно. Потолок
 * тот же, что у живого прогона, и по той же причине: у волны, чей терминальный манифест не доехал
 * (закрыли вкладку, кончились попытки), прогон рождается только по серверному дедлайну — позже 15 минут.
 * С коротким потолком мастер успевал объявить «имён нет, заполните руками», а имена и отбор
 * доезжали через несколько минут после этого, когда на них уже никто не смотрел.
 */
const FOLLOW_CEILING_MS = LIVE_RUN_CEILING_MS;
/**
 * Шаг мастера, на котором применили таблицу: проект → вид шага. Вердикт таблицы проектный, а ожидание
 * фотографий принадлежит ровно тому шагу, куда таблицу принесли, — без этой памяти «Таблица ждёт фото»
 * вставала бы на каждом фото-шаге. Живёт в браузере: у прогона таблицы шага нет, и сервер о нём не знает.
 */
const TABLE_STEP_STORAGE_KEY = 'fc.table-step';

/** Фото-шаг мастера: чьи портреты или прочие кадры он собирает. */
export type PhotoStepKind = 'student' | 'teacher' | 'other';
const PHOTO_STEP_KINDS: ReadonlySet<string> = new Set<PhotoStepKind>(['student', 'teacher', 'other']);

function isPhotoStepKind(value: unknown): value is PhotoStepKind {
  return typeof value === 'string' && PHOTO_STEP_KINDS.has(value);
}

function restoreTableSteps(): ReadonlyMap<string, PhotoStepKind> {
  const result = new Map<string, PhotoStepKind>();
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(TABLE_STEP_STORAGE_KEY) ?? '{}');
    if (typeof parsed !== 'object' || parsed === null) return result;
    for (const [projectId, step] of Object.entries(parsed)) {
      if (isPhotoStepKind(step)) result.set(projectId, step);
    }
  } catch {
    // Нечитаемое хранилище — просто нет памяти о шаге: плашку покажет первый фото-шаг.
  }

  return result;
}

/** Прогон дальше не изменится: PARTIAL держим в опросе — он дожимается по мере готовности лиц. */
const TERMINAL: ReadonlySet<ImportRun['status']> = new Set<ImportRun['status']>(['APPLIED', 'FAILED']);
/** Прогоны, чей набор уже несёт связи «кадр → персона»: только у них есть смысл дочитывать его. */
const SET_BEARING: ReadonlySet<ImportRun['status']> = new Set<ImportRun['status']>(['APPLIED', 'PARTIAL']);
/**
 * Порядок статусов для СНИМКОВ ОПРОСА: в списке прогонов один и тот же прогон едет только вперёд
 * (PARTIAL дожимается в APPLIED, терминал не откатывается), поэтому тик с более ранним статусом
 * известного прогона — устаревший снимок, а не движение назад.
 */
const STATUS_RANK: Readonly<Record<ImportRun['status'], number>> = {
  EXTRACTING: 0,
  PREVIEW: 1,
  APPLYING: 2,
  PARTIAL: 3,
  APPLIED: 4,
  FAILED: 4,
};

/**
 * Арбитраж свежести двух снимков одного прогона (async-state-mutation.md п.5). Прогон С НАБОРОМ —
 * ответ apply/settle этого самого прогона, то есть его пересчёт целиком: статус, отчёт и набор в нём
 * одного возраста, и ранг ему не судья. Повтор применённого прогона законно роняет APPLIED в PARTIAL —
 * сервер держит статус прогона и переписывает его по остатку ожидающих лица кадров, — так что по рангу
 * победил бы старый объект, и новый набор считался бы по допрогонному отчёту. Список прогонов набора
 * не несёт, поэтому тик опроса этой дорогой не проходит. Дальше побеждает более поздний статус, при
 * равных — более поздний `updatedAt` (сервер пишет его на каждом apply): устаревший GET списка,
 * доехавший после ответа apply при живом опросе (PARTIAL↔PARTIAL), иначе на тик показывал бы новый
 * набор со старым отчётом — «0 из 20». Без `updatedAt` у одного из двух — пришедший. Один писатель
 * для опроса и для settle.
 */
function fresherRun(known: ImportRun | undefined, incoming: ImportRun): ImportRun {
  if (incoming.set || !known) return incoming;
  const knownRank = STATUS_RANK[known.status];
  const incomingRank = STATUS_RANK[incoming.status];
  if (knownRank !== incomingRank) return knownRank > incomingRank ? known : incoming;
  const knownAt = Date.parse(known.updatedAt ?? '');
  const incomingAt = Date.parse(incoming.updatedAt ?? '');

  return Number.isFinite(knownAt) && Number.isFinite(incomingAt) && knownAt > incomingAt ? known : incoming;
}

/** Итог автоподстановки имён одной волны — строка «Имена подставлены из файлов: N». */
export interface ImportWaveApplied {
  /** Прогон, давший эти числа: по нему вызывающий узнаёт свой разбор архива — волны у того нет. */
  readonly runId: string;
  /** Волна загрузки; у прогона архива её нет: zip приезжает мимо очереди и волны не метит. */
  readonly uploadBatchId?: string;
  /** Профиль прогона: им плашка и таблетка называют ИСТОЧНИК имён — папки, таблица или названия файлов. */
  readonly profileId: string;
  /** Персон, получивших ФИО из названий файлов: созданные плюс дозаполненные. */
  readonly names: number;
  /**
   * Персон в наборе прогона — сколько имён он РАСПОЗНАЛ; null — набор ещё не дочитан. Больше `names`,
   * когда часть имён уже сидела в ростере: плашка объявляет распознанное, а не применённое, иначе её
   * число расходится со своим же хвостом ролей и с витриной «Посмотреть имена».
   */
  readonly total: number | null;
  /** Учеников в наборе прогона; null — набор ещё не дочитан, роли назвать нечем. */
  readonly students: number | null;
  /** Педагогов в наборе прогона; null — набор ещё не дочитан. */
  readonly teachers: number | null;
  /** Папок людей ВЕРХНЕГО уровня (педагоги из своей подпапки сюда не входят); null — профиль не папочный. */
  readonly foldersWithNames: number | null;
  /** Кадров во всём разобранном дереве; null — профиль не папочный. */
  readonly files: number | null;
  /** Имя перетащенной папки съёмки, если разбор его выделил. */
  readonly rootName?: string;
  /** Набор прогона, когда дочитан: только в нём лежат пути кадров и связи «кадр → персона». */
  readonly set: ImportSet | null;
}

/**
 * Итог применённой таблицы одним значением — его читают оба шага мастера. `waiting` — фотографий ещё
 * нет, связывать не с чем (колонка могла быть найдена или нет): таблица принята и ждёт их; `applied` — колонка нашлась,
 * `matched` из `total` строк село на кадры, `unmatched` осталось без них; `belowThreshold` — похожая
 * колонка совпала меньше чем с половиной строк, поэтому НЕ взята; `none` — колонки нет при
 * загруженных фотографиях. `null` — сказать пока нечего (волна идёт, таблицы нет, набор не дочитан).
 */
export type ImportTableState =
  | { readonly kind: 'waiting' }
  | {
      readonly kind: 'applied';
      readonly matched: number;
      readonly total: number;
      readonly unmatched: number;
      readonly unmatchedNames: readonly string[];
      readonly missingNames: readonly string[];
      readonly unplacedNames: readonly string[];
    }
  | { readonly kind: 'belowThreshold' }
  | { readonly kind: 'none' };

/**
 * Следит за автопрогоном импорта помеченной волны. Прогон рождается ПОСЛЕ того, как волна обработана:
 * сервер создаёт его, когда она затихла, — поэтому опрос статуса проекта тут не годится. Опрашиваем
 * только помеченные волны: у остальных прогона не будет.
 */
@Injectable()
export class ImportWaveOutcomeService {
  private readonly api = inject(PersonImportApiService);
  private readonly coordinator = inject(PhotoUploadCoordinatorService);
  private readonly importStore = inject(PersonImportStore);
  private readonly injector = inject(Injector);

  private readonly projectId = signal<string | null>(null);
  private readonly runs = signal<readonly ImportRun[]>([]);
  private pollSub: Subscription | null = null;
  private pollGeneration = 0;
  private startedAt = 0;
  /** Волны, чей итог уже показан: они больше не держат опрос, но строка по ним остаётся на экране. */
  private readonly settled = signal<ReadonlySet<string>>(new Set());
  /** Прогоны, за которыми попросили следить отдельно: у разбора архива волны нет, и без этого опрос
   *  засыпал бы раньше, чем сервер вообще создаст прогон. */
  private readonly tracked = signal<ReadonlySet<string>>(new Set());
  /** Дочитанные наборы прогонов: runId → набор. Список прогонов их не несёт. */
  private readonly setsByRun = signal<ReadonlyMap<string, ImportSet>>(new Map());
  /** Прогоны, за набором которых уже сходили: один запрос на прогон, метку снимает только отказ. */
  private setAsked = new Set<string>();
  /** Шаг применения таблицы по проектам — переживает перезагрузку. */
  private readonly tableSteps = signal<ReadonlyMap<string, PhotoStepKind>>(restoreTableSteps());
  /** Поколение дочитки: смена проекта (в том числе возврат в прежний) обесценивает ответы в пути. */
  private hydrateGeneration = 0;

  private readonly waveIds = computed<ReadonlySet<string>>(() => {
    const projectId = this.projectId();
    return projectId ? this.coordinator.importWaveIdsFor(projectId)() : new Set<string>();
  });

  /** Отслеживаемый прогон, которого в списке ещё нет или который ещё не терминален, — опрос держит он. */
  private readonly trackedOpen = computed<boolean>(() => {
    const ids = this.tracked();
    if (ids.size === 0) return false;
    const runs = this.runs();
    return [...ids].some(id => !runs.some(run => run.id === id && TERMINAL.has(run.status)));
  });

  /** Опрос упёрся в потолок ожидания — прогона так и нет, «вечное ожидание имён» честно гасим. */
  private readonly followCeilingReached = signal(false);
  /** Потолок живого прогона вышел: сервер своё ожидание лиц уже закрыл, ждать дальше нечего. */
  private readonly liveCeilingReached = signal(false);
  /** Хотя бы один ответ /import/runs получен: до него о прогонах без помеченных волн судить нечем. */
  private readonly fetchedOnce = signal(false);
  /** Тики опроса, окончившиеся обрывом. Потолки ожидания пересчитывает только эффект, а будят его одни
   *  ответы: с молчащим /import/runs не срабатывал бы ровно тот потолок, что заведён против «вечного
   *  ожидания имён». */
  private readonly failedProbes = signal(0);
  /** Сервер ответил определённым «нет» (выключенный импорт — 404, организатору — 403): прогона не будет
   *  никогда, и ждать его до общего потолка значит держать «Получаем имя…» полчаса на пустом месте. */
  private readonly definiteOff = signal(false);

  /**
   * Прогон, чьи имена ещё доедут: волновой или PARTIAL любого профиля — применённая таблица волной не
   * ключуется, но её связи «кадр → персона» ждут лиц. Превью-прогоны диалога идут без uploadBatchId,
   * терминальными не становятся никогда и ожидания держать не должны — поэтому пропуском служит ровно
   * PARTIAL, статус уже применённого прогона.
   */
  private readonly liveRun = computed<boolean>(() =>
    this.runs().some(run => !TERMINAL.has(run.status) && (!!run.uploadBatchId || run.status === 'PARTIAL')),
  );

  /**
   * Живой ВОЛНОВОЙ прогон: кадры волны ещё раскладываются по людям. PARTIAL без волны — таблица или
   * архив — сюда не входит: он уже применён, сервер связал кадры с персонами и дожимает одни лица, а
   * ждать этого дожима значит ждать серверного дедлайна лица (полчаса).
   */
  private readonly liveWaveRun = computed<boolean>(() =>
    this.runs().some(run => !TERMINAL.has(run.status) && !!run.uploadBatchId),
  );

  /** Общий разбор ожидания; `live` — что именно вызывающий считает живым прогоном. */
  private incoming(live: boolean): boolean {
    // Отказ по существу главнее любого потолка: сервер уже сказал, что импорта здесь нет.
    if (this.definiteOff()) return false;
    // Живой прогон отодвигает потолок ожидания до своего собственного: потолок ожидания отсчитан от входа
    // на первый фото-шаг, а не от появления прогона, и гасить сверку, пока сервер ещё дожимает имена,
    // значит врать про пришедший итог. Но и живой прогон не вечен — после LIVE_RUN_CEILING_MS
    // серверный дедлайн ожидания лица уже вышел, и дальше действует обычный потолок.
    if (live && !this.liveCeilingReached()) return true;
    if (this.followCeilingReached()) return false;
    // Волна после F5 (когда память координатора пуста) помеченной волны не имеет, но /import/runs про неё знает.
    const terminal = new Set<string>();
    for (const run of this.runs()) {
      if (TERMINAL.has(run.status) && run.uploadBatchId) terminal.add(run.uploadBatchId);
    }

    return [...this.waveIds()].some(id => !terminal.has(id));
  }

  /**
   * Имена «в пути»: у какой-то помеченной волны прогон ещё не терминален (APPLIED/FAILED). Пока
   * true, безымянные карточки — переходное состояние («Получаем имя…»), а не повод просить
   * пользователя заполнять имена руками.
   */
  readonly namesIncoming = computed<boolean>(() => this.incoming(this.liveRun()));

  /**
   * То же ожидание глазами ТАБЛИЦЫ: её вердикт держит только едущая волна кадров. PARTIAL применённой
   * таблицы — не ожидание: связи «кадр → персона» уже записаны, и её собственные числа от дожима лиц
   * не изменятся. Без этого вердикт вставал бы через полчаса — намного позже, чем открылось
   * «Продолжить», у которого ждать уже нечего.
   */
  private readonly tableWaveIncoming = computed<boolean>(() => this.incoming(this.liveWaveRun()));

  /** Прогон помеченной волны, доведённый до конца. null — ещё не готов или волна не импортная. */
  readonly applied = computed<ImportWaveApplied | null>(() => {
    const ids = this.waveIds();

    for (const run of this.runs()) {
      // Папочный прогон — оба папочных профиля, обычный и отбор из папок, — отвечает за себя сам: он
      // приезжает и волной загрузки, и архивом (zip волны не метит), а числа его отчёта финальны уже на
      // применении — в PARTIAL он лишь ждёт лиц остальных кадров, и держать из-за них плашку до
      // дедлайн-крона (полчаса) значило бы не показать её вовсе.
      const folder = isFolderProfile(run.profileId) && SET_BEARING.has(run.status);
      if (!folder && (ids.size === 0 || !run.uploadBatchId || !ids.has(run.uploadBatchId) || !TERMINAL.has(run.status))) {
        continue;
      }

      if (run.status === 'FAILED') continue;
      const names = namesApplied(run);
      if (names === 0) continue;
      // Роли живут только в наборе прогона: пока его не дочитали, счётчиков нет — строка обходится без них.
      const set: ImportSet | undefined = run.set ?? this.setsByRun().get(run.id);
      const roles = set ? personRoleCounts(set) : null;
      return {
        runId: run.id,
        ...(run.uploadBatchId ? { uploadBatchId: run.uploadBatchId } : {}),
        profileId: run.profileId,
        names,
        total: set?.persons.length ?? null,
        students: roles?.students ?? null,
        teachers: roles?.teachers ?? null,
        // Раскладка лежит в отчёте плоско и приезжает уже со списком прогонов — дочитки набора не ждёт.
        foldersWithNames: run.report?.foldersWithNames ?? null,
        files: run.report?.files ?? null,
        ...(run.report?.rootName !== undefined ? { rootName: run.report.rootName } : {}),
        set: set ?? null,
      };
    }

    return null;
  });

  /**
   * Последняя применённая таблица проекта. Её прогон волной загрузки не ключуется, поэтому после
   * перезагрузки о нём помнит только сервер: без него применённая таблица подписалась бы плашкой по
   * названиям файлов. Прогон, не севший ни на одного человека, тоже в счёт: таблетка и разбор
   * нехватки считают СТРОКИ таблицы, а не совпадения, и «Совпало 0 имён из 5» — законный итог.
   * `/import/runs` отдаёт прогоны от новых к старым, так что первый подходящий и есть самый новый.
   */
  readonly latestAppliedTable = computed<ImportRun | null>(
    () => this.runs().find(run => run.profileId === 'table' && SET_BEARING.has(run.status)) ?? null,
  );

  /** Набор применённой таблицы: витрине «Посмотреть таблицу» он заменяет файл после перезагрузки. */
  readonly appliedTableSet = computed<ImportSet | null>(() => {
    const run = this.latestAppliedTable();
    return run ? (run.set ?? this.setsByRun().get(run.id) ?? null) : null;
  });

  /**
   * ФИО всех строк применённой таблицы — то, что таблица принесла, независимо от того, нашлась ли под
   * именем распознанная персона. Знаменатель сверки «имена ↔ люди со съёмки». Пусто, пока набор
   * прогона не дочитан: список строк живёт только в нём.
   */
  readonly appliedTableNames = computed<readonly string[]>(() => {
    const run = this.latestAppliedTable();
    if (!run) return [];
    const set: ImportSet | undefined = run.set ?? this.setsByRun().get(run.id);

    return set ? tableNames(set) : [];
  });

  /**
   * Итог применённой таблицы — ОДИН источник для обоих шагов. Ждёт ровно того, что ещё может его
   * изменить: пока идёт волна имён, вердикта нет — половина кадров не приехала, и любое «имена не
   * совпали» было бы ложной тревогой. Готовность СТРАНИЦЫ вердикт не держит: гейт распознавания
   * считает весь проект и взводится заново от пачки, брошенной на любом другом шаге, — из-за неё
   * вердикт таблицы не показывался вовсе, хотя её собственные прогоны давно закрыты. Не держит его и
   * PARTIAL самой таблицы: сервер уже связал кадры, дожимает одни лица — и вердикт вставал бы через
   * полчаса, когда «Продолжить» давно открыто. Порядок разбора: недобор колонки главнее её судьбы —
   * при нём сервер тоже пишет `fileColumn: 'none'`, но это не отказ, а «имена файлов не совпали».
   */
  readonly tableState = computed<ImportTableState | null>(() => {
    if (this.tableWaveIncoming()) return null;
    const run = this.latestAppliedTable();
    if (!run) return null;
    const set: ImportSet | undefined = run.set ?? this.setsByRun().get(run.id);
    // Разбор таблицы живёт только в наборе прогона: пока он не дочитан, молчание честнее догадки.
    const table = set?.table;
    if (!set || !table) return null;
    if (table.fileColumnBelowThreshold === true) return { kind: 'belowThreshold' };
    // Набор — сохранённый JSON, отданный как есть: у таблицы, применённой до появления поля
    // `fileColumn`, его нет вовсе. Отсутствие — «сказать нечего», а не доказанный отказ: иначе проект,
    // чьи имена подставлены давным-давно, встречал бы жёлтое «в таблице нет колонки с именами файлов».
    const fileColumn: ImportTableInfo['fileColumn'] | undefined = table.fileColumn;
    if (fileColumn === undefined) return null;
    if (fileColumn === 'deferred') return { kind: 'waiting' };
    if (fileColumn !== 'resolved') return { kind: 'none' };
    const counts = rowCounts(previewRows(set), run.report?.issues ?? []);

    return {
      kind: 'applied',
      matched: counts.matched,
      total: counts.total,
      unmatched: counts.unmatched,
      unmatchedNames: counts.unmatchedNames,
      missingNames: counts.missingNames,
      unplacedNames: counts.unplacedNames,
    };
  });

  /**
   * Шаг, на который принесли таблицу этого проекта. null — памяти нет: таблицу применили не из мастера
   * (на странице «Лица»), в другом браузере или до появления этой памяти.
   */
  readonly tableStep = computed<PhotoStepKind | null>(() => {
    const projectId = this.projectId();
    return projectId ? (this.tableSteps().get(projectId) ?? null) : null;
  });

  /**
   * Имя человека под каждым кадром: id файла → ФИО. Собирается по ВСЕМ прогонам проекта — связь кадра
   * с персоной живёт в наборе того прогона, который её создал (у таблицы без колонки файла связей нет,
   * их даёт файловый прогон той же волны). Поздний прогон переписывает раннюю подпись того же кадра,
   * поэтому идём с конца: /import/runs отдаёт прогоны от новых к старым.
   */
  readonly captions = computed<ReadonlyMap<string, string>>(() => {
    const sets = this.setsByRun();
    const captions = new Map<string, string>();
    for (const run of [...this.runs()].reverse()) {
      const set: ImportSet | undefined = run.set ?? sets.get(run.id);
      if (!set) continue;
      for (const [fileId, name] of captionsByFile(set)) captions.set(fileId, name);
    }

    return captions;
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => this.stopPolling());
    effect(() => {
      const projectId = this.projectId();
      const ids = this.waveIds();
      const applied = this.applied();
      const probed = this.fetchedOnce();
      const nonTerminal = this.runs().some(run => !TERMINAL.has(run.status));
      const trackedOpen = this.trackedOpen();
      const live = this.liveRun();
      // Отказавший тик — тоже повод пересчитать потолки: другого будильника у них нет.
      this.failedProbes();
      const off = this.definiteOff();
      untracked(() => {
        // Определённое «нет» гасит опрос сразу: следующие тики принесут ровно тот же отказ.
        if (!projectId || off) {
          this.stopPolling();
          return;
        }

        // Итог показан или потолок ожидания вышел — ходить больше некуда. Прогон без волны (архив)
        // ничего не закрывает: закрывать нечего, его держит собственная слежка.
        const settledWave = applied?.uploadBatchId;
        if (settledWave) {
          this.settled.update(seen => new Set([...seen, settledWave]));
        }

        const open = [...ids].some(id => !this.settled().has(id));
        const elapsed = Date.now() - this.startedAt;
        const ceiling = elapsed >= FOLLOW_CEILING_MS;
        const liveCeiling = elapsed >= LIVE_RUN_CEILING_MS;
        this.followCeilingReached.set(ceiling);
        this.liveCeilingReached.set(liveCeiling);
        // Один пробный запрос нужен и без помеченных волн: волна после F5 живёт только
        // в /import/runs; дальше опрос держат открытые волны, нетерминальные и отслеживаемые прогоны.
        // Живой прогон опрос не отпускает и после потолка ожидания — тот сторожит ожидание прогона,
        // которого нет, а не сервер, который прямо сейчас дожимает имена; своим потолком живой прогон
        // всё-таки связан.
        if ((live && !liveCeiling) || (!ceiling && (open || nonTerminal || trackedOpen || !probed)))
          this.ensurePolling(projectId);
        else this.stopPolling();
      });
    });
  }

  start(projectId: string): void {
    if (this.projectId() === projectId) return;
    this.stopPolling();
    this.runs.set([]);
    this.settled.set(new Set());
    this.tracked.set(new Set());
    this.setsByRun.set(new Map());
    this.setAsked = new Set();
    this.hydrateGeneration++;
    this.startedAt = Date.now();
    this.followCeilingReached.set(false);
    this.liveCeilingReached.set(false);
    this.fetchedOnce.set(false);
    this.definiteOff.set(false);
    this.projectId.set(projectId);
  }

  /**
   * Взять прогон под слежку. Разбор архива волны не метит, а опрос без открытых волн затихает после
   * первого пробного запроса — прогон, рождённый позже, так и остался бы непрочитанным. Окно ожидания
   * при этом открывается заново: прогон только что создан, и старый отсчёт к нему отношения не имеет.
   */
  trackRun(runId: string): void {
    this.startedAt = Date.now();
    this.followCeilingReached.set(false);
    this.tracked.update(ids => new Set(ids).add(runId));
  }

  /**
   * Таблицу применили, когда фотографий этих людей в проекте ещё не было: их строки остались без
   * связи «кадр → персона», и догруженный кадр сам к имени не приедет. Повторное применение того же
   * прогона перечитывает кадры проекта заново — имя садится на нового человека. Живёт у страницы, а
   * не у шага: догрузить фото можно на любом фото-шаге, и триггер на «Проверке персон» пропускал
   * все пачки до неё. `tableState() === null` — волна ещё идёт: тогда взводимся заново, чтобы каждая
   * следующая пачка получила свой повтор. Взведён и на маунте страницы: таблицу могли применить на
   * «Проверке персон» или в другом браузере и там же догрузить кадры — повтор на открытии их
   * подхватывает.
   */
  reapplyTableOnSettle(page: { projectId: Signal<string>; refreshPersons: (projectId: string) => Promise<void> }): EffectRef {
    let armed = true;
    return effect(
      () => {
        const projectId = page.projectId();
        const state = this.tableState();
        const table = this.latestAppliedTable();
        if (state === null) {
          armed = true;

          return;
        }

        const photos = projectId ? this.coordinator.projectTotal(projectId)() : null;
        if (!projectId || !table || !armed || !tableNeedsReapply(state, photos)) return;
        armed = false;
        untracked(() => void this.reapplyTable(projectId, table.id, table.overrides ?? {}, page.refreshPersons));
      },
      { injector: this.injector },
    );
  }

  private async reapplyTable(
    projectId: string,
    runId: string,
    overrides: ImportOverrides,
    refreshPersons: (projectId: string) => Promise<void>,
  ): Promise<void> {
    const run = await this.importStore.apply(projectId, runId, overrides);
    // Ответ apply несёт ПЕРЕСЧИТАННЫЙ набор того же прогона: за набором сервис ходит один раз, и без
    // этой передачи вердикт таблицы до конца захода читался бы по допрогонному — «ждём фото» навсегда.
    if (run) this.noteRuns([run]);
    await refreshPersons(projectId);
  }

  /** Запомнить шаг применения таблицы: ожидание фотографий принадлежит ему одному. */
  noteTableStep(step: PhotoStepKind): void {
    const projectId = this.projectId();
    if (!projectId) return;
    const next = new Map(this.tableSteps()).set(projectId, step);
    try {
      localStorage.setItem(TABLE_STEP_STORAGE_KEY, JSON.stringify(Object.fromEntries(next)));
    } catch {
      // Квота или приватный режим: память о шаге не критична — плашку покажет первый фото-шаг.
    }

    this.tableSteps.set(next);
  }

  /**
   * Прогоны, добытые мимо опроса (гейт дожимает волны до открытия «Проверки персон»), — вливаем их
   * по id: известный прогон обновляем на месте, новый кладём в голову (список идёт от новых к старым).
   * Опрос при этом не трогаем: своё поколение он ведёт сам, а его поздний ответ отсекает арбитраж
   * свежести — тот же, что и здесь. Ответ чужого проекта (или до старта) в список не попадает и за
   * «получен хотя бы один ответ» не считается; пустой ответ проекту не приписать, он засчитывается.
   */
  noteRuns(runs: readonly ImportRun[]): void {
    const projectId = this.projectId();
    if (!projectId) return;
    const own: readonly ImportRun[] = runs.filter(item => item.projectId === projectId);
    if (own.length === 0 && runs.length > 0) return;

    this.runs.update(current => {
      // Дубль id внутри одного ответа — тот же арбитраж, что и между ответами.
      const incoming = new Map<string, ImportRun>();
      for (const item of own) incoming.set(item.id, fresherRun(incoming.get(item.id), item));
      const known = new Set(current.map(item => item.id));
      const merged = current.map(item => {
        const next = incoming.get(item.id);
        return next ? fresherRun(item, next) : item;
      });
      return [...[...incoming.values()].filter(item => !known.has(item.id)), ...merged];
    });
    // Список прогонов набор НЕ несёт намеренно, поэтому набор, приехавший другой дорогой — ответом
    // apply или итогом волны, — единственная свежая копия: следующий тик опроса заменит объект
    // прогона, и встроенный `set` из него пропадёт. Без этой записи повторно применённая таблица
    // читалась бы по набору, добытому дочиткой ОДИН раз (`setAsked`), — то есть по допрогонному.
    const sets: readonly (readonly [string, ImportSet])[] = own
      .filter((item): item is ImportRun & { set: ImportSet } => !!item.set)
      .map(item => [item.id, item.set] as const);
    if (sets.length > 0) this.setsByRun.update(current => new Map([...current, ...sets]));
    this.fetchedOnce.set(true);
  }

  private ensurePolling(projectId: string): void {
    if (this.pollSub) return;
    // exhaustMap + потолок на запрос: тик, чей ответ ещё в пути, не рвём (async-state-mutation.md п.4);
    // генерационный стамп отбрасывает ответ, доехавший на уже другой проект.
    const generation = ++this.pollGeneration;
    this.pollSub = timer(0, POLL_INTERVAL_MS)
      .pipe(
        exhaustMap(() =>
          this.api.listRuns(projectId).pipe(
            timeout(POLL_TIMEOUT_MS),
            catchError((error: unknown) => {
              if (isDefiniteOff(error)) this.definiteOff.set(true);
              else this.failedProbes.update(count => count + 1);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(runs => {
        if (generation !== this.pollGeneration || this.projectId() !== projectId) return;
        // Состав списка — от сервера; статус известного прогона — от более свежего снимка: exhaustMap не
        // отменяет ушедший GET, и его ответ, выданный ДО settle, приезжает ПОСЛЕ noteRuns.
        this.runs.update(current => {
          const known = new Map(current.map(item => [item.id, item]));
          return (runs ?? []).map(item => fresherRun(known.get(item.id), item));
        });
        this.fetchedOnce.set(true);
        this.hydrateSets(projectId, runs ?? []);
      });
  }

  /**
   * Дочитывание набора применённого прогона. `/import/runs` отдаёт список НАМЕРЕННО без `set`
   * (get-import-runs.service.ts: «снимок набора здесь лишний вес, его отдаёт getRun»), а связь
   * «кадр → персона» живёт только в наборе — без дочитки подписей под кадрами не будет никогда.
   * Один УДАВШИЙСЯ запрос на прогон; прогон, догнанный до применённого поздним тиком, дочитается на
   * этом тике. Сразу за отказом одна повторная попытка: опрос над применённым прогоном затихает, и
   * следующего тика может не быть вовсе. Отказ шаг не роняет, но и метку не оставляет: один сетевой
   * сбой выключал сверку имён до конца захода в мастер, поэтому отказавший прогон возвращается в
   * очередь и повторяется на каждом следующем тике — то есть пока жив сам опрос, до потолка ожидания.
   */
  private hydrateSets(projectId: string, runs: readonly ImportRun[]): void {
    const generation = this.hydrateGeneration;
    for (const run of runs) {
      if (run.set || !SET_BEARING.has(run.status) || this.setAsked.has(run.id)) continue;
      this.setAsked.add(run.id);
      this.api
        .getRun(projectId, run.id)
        .pipe(
          timeout(POLL_TIMEOUT_MS),
          retry({ count: 1, delay: SET_RETRY_DELAY_MS }),
          catchError(() => {
            this.setAsked.delete(run.id);
            return EMPTY;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(full => {
          // Ответ, доехавший на уже другой проект или в новый заход, в карту не попадает
          // (async-state-mutation.md п.1: генерационный стамп снят до запроса).
          const set: ImportSet | undefined = full?.set;
          if (!set || generation !== this.hydrateGeneration || this.projectId() !== projectId) return;
          this.setsByRun.update(current => new Map(current).set(run.id, set));
        });
    }
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.pollGeneration++;
  }
}

/**
 * Тот же прогон таблицы надо применить ЗАНОВО: его вердикт ещё изменят кадры, уже лежащие в проекте.
 * `applied` со строками БЕЗ КАДРА — кадры догрузили после применения; строки, чьё имя не легло при
 * найденном кадре (спорное имя, занятое лицо), новые фото не лечат, и повтор по ним — лишний
 * POST на каждый маунт. `waiting` при загруженных
 * кадрах — таблицу применили к пустому проекту, и разбор колонки файла остался отложенным навсегда:
 * сервер решает судьбу колонки в момент применения (`fileColumn: 'deferred'`, parsers/table.ts), а
 * сам к догруженным кадрам не возвращается — без повтора все 20 строк так и стоят без имён.
 * `belowThreshold` — тот же случай на полпути: колонку считали против кадров, лежавших в проекте в
 * момент применения, и следующая пачка меняет счёт совпадений. Вердикт по НЕПОЛНОМУ набору кадров
 * предварителен, а не окончателен, поэтому повтор нужен на тех же условиях, что и `waiting`.
 */
export function tableNeedsReapply(state: ImportTableState, projectPhotos: number | null): boolean {
  if (state.kind === 'applied') return state.missingNames.length > 0;

  return (state.kind === 'waiting' || state.kind === 'belowThreshold') && (projectPhotos ?? 0) > 0;
}

/** Созданные + дозаполненные + уже сидевшие в леджере персоны — те, кому имя дал этот прогон. */
export function namesApplied(run: ImportRun): number {
  const counters = run.report?.counters;
  if (!counters) return 0;
  // SKIPPED — имя уже сидело в леджере этого прогона (повторное применение таблицы после догрузки
  // фото): оно по-прежнему подставлено таблицей, без него повтор обнулял бы счёт и прятал таблицу.
  // MATCHED — дописывать было нечего, у человека уже стоит ровно это имя: строка таблицы села, и ноль
  // здесь врал бы про «ничего не подставилось».
  return (counters.CREATED ?? 0) + (counters.UPDATED ?? 0) + (counters.SKIPPED ?? 0) + (counters.MATCHED ?? 0);
}
